/**
 * Helpers de árbol para las estructuras maestras.
 * Espejo de server/src/master-structures/master-structures.helpers.ts: la jerarquía
 * se deriva del prefijo del código ("1", "1.01", "1.01.01", "1.01.01.01").
 */

export const MASTER_MAX_DEPTH = 4;

export const MASTER_STRUCTURE_TYPES = {
  ACTIVOS: 'ACTIVOS',
  PASIVOS_PATRIMONIO: 'PASIVOS_PATRIMONIO',
  RESULTADO: 'RESULTADO',
};

/** Estructura a la que corresponde una cuenta contable, según su sección. */
export const STRUCTURE_BY_SECTION = {
  1: MASTER_STRUCTURE_TYPES.ACTIVOS,
  2: MASTER_STRUCTURE_TYPES.PASIVOS_PATRIMONIO,
  3: MASTER_STRUCTURE_TYPES.RESULTADO,
  4: MASTER_STRUCTURE_TYPES.RESULTADO,
};

export function accountDepth(key) {
  return String(key || '')
    .split('.')
    .filter(Boolean).length;
}

export function parentKeyOf(key) {
  const parts = String(key || '')
    .split('.')
    .filter(Boolean);
  if (parts.length <= 1) return '';
  return parts.slice(0, -1).join('.');
}

export function sectionOf(key) {
  return (
    String(key || '')
      .split('.')
      .filter(Boolean)[0] || ''
  );
}

/** Estructura maestra que corresponde a una cuenta contable de origen. */
export function structureTypeForSourceKey(sourceKey) {
  return STRUCTURE_BY_SECTION[sectionOf(sourceKey)] || null;
}

export function findNodeByKey(nodes, key) {
  for (const node of nodes || []) {
    if (node?.key === key) return node;
    if (node?.children?.length) {
      const found = findNodeByKey(node.children, key);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Referencia única de una cuenta maestra. La `key` es LOCAL a cada estructura
 * (todas arrancan en su propia raíz), así que indexar solo por key cruzaría
 * cuentas de estructuras distintas. El backend la identifica igual: (type, key).
 */
export function masterAccountRef(structureType, key) {
  return `${structureType}::${key}`;
}

/**
 * Índice de todas las cuentas INDIVISIBLES (las que no agrupan a ninguna otra):
 * `${structureType}::${key}` → { key, name, label, structureType, structureName, path }.
 *
 * Antes eran "las de nivel 4". Los árboles ya no tienen profundidad fija: cada
 * rama llega hasta donde el CSV la detalla, así que la condición es no tener
 * hijas.
 */
export function buildMasterAccountIndex(structures) {
  const index = new Map();

  const walk = (nodes, structure, path) => {
    (nodes || []).forEach((node) => {
      if (!node?.key) return;
      const nextPath = [...path, node.name];
      if (!node.children?.length) {
        index.set(masterAccountRef(structure.type, node.key), {
          key: node.key,
          name: node.name,
          label: node.label || `${node.key} ${node.name}`,
          structureType: structure.type,
          structureName: structure.name,
          path: nextPath.join(' > '),
        });
      }
      if (node.children?.length) walk(node.children, structure, nextPath);
    });
  };

  (structures || []).forEach((structure) => walk(structure.accounts, structure, []));
  return index;
}

/**
 * Aplana una estructura a las cuentas INDIVISIBLES, que son las asignables.
 *
 * El encabezado de sección es la ruta de las agrupadoras que quedan por encima.
 * Como las ramas ya no tienen todas la misma profundidad, la ruta puede ser de
 * una, dos o tres agrupadoras — o de ninguna, si el grupo del CSV no tenía más
 * detalle y quedó como una cuenta suelta.
 */
export function extractMasterLeafOptions(structure) {
  const options = [];
  if (!structure?.accounts?.length) return options;

  const walk = (node, ancestros) => {
    if (node.children?.length) {
      node.children.forEach((child) => walk(child, [...ancestros, node]));
      return;
    }

    const sectionKey = ancestros.map((a) => a.key).join('-');
    const lastSectionKey = options[options.length - 1]?.sectionKey;
    if (sectionKey && sectionKey !== lastSectionKey) {
      options.push({
        key: `section-${sectionKey}`,
        value: null,
        text: ancestros.map((a) => a.name).join(' > '),
        isSection: true,
        sectionKey,
      });
    }

    options.push({
      key: node.key,
      value: node.key,
      /**
       * Sin la clave: se parece a un código contable —"1.01.01" existe en los
       * dos árboles y significa cosas distintas— y acá no hace falta, porque el
       * encabezado de sección ya dice de qué rama viene la cuenta.
       */
      text: node.name,
      label: node.name,
      account: node,
      sectionKey,
    });
  };

  structure.accounts.forEach((root) => walk(root, []));
  return options;
}

/**
 * Aplana TODOS los niveles de una estructura, no solo las hojas.
 *
 * El constructor de fórmulas necesita poder operar sobre un grupo entero (por
 * ejemplo "Gastos de Administración y Ventas", que es un nivel 1): el total de
 * un nodo es el de su subárbol.
 */
export function extractMasterAllLevelOptions(structure) {
  const options = [];
  if (!structure?.accounts?.length) return options;

  const walk = (node, level) => {
    options.push({
      key: node.key,
      value: node.key,
      // La indentación con guiones deja ver la jerarquía en una lista plana.
      // La sangría marca el nivel; la clave se omite por lo mismo que arriba.
      text: `${'— '.repeat(Math.max(0, level - 1))}${node.name}`,
      label: node.name,
      account: node,
      level,
    });
    (node.children || []).forEach((child) => walk(child, level + 1));
  };

  structure.accounts.forEach((root) => walk(root, 1));
  return options;
}

/** El valor con el que viaja una cuenta cuando la lista mezcla estructuras. */
export const refDeCuentaMaestra = (structureType, key) => `${structureType}::${key}`;

export const partirRefDeCuentaMaestra = (ref) => {
  const [structureType, ...resto] = String(ref || "").split("::");
  return resto.length
    ? { structureType, key: resto.join("::") }
    : { structureType: null, key: structureType || "" };
};

/**
 * Las cuentas de VARIAS estructuras en una sola lista, con un encabezado por
 * estructura.
 *
 * El balance se arma sobre dos —Activos y Pasivos y Patrimonio— y un campo
 * calculado suyo puede necesitar cuentas de las dos: "capital de trabajo" es
 * activo circulante menos pasivo circulante. Con una sola lista por estructura
 * ese campo no se podía construir.
 *
 * ⚠️ El valor lleva la estructura adentro (`ACTIVOS::1.01`): las claves
 * arrancan en "1" en las dos, así que sin el prefijo dos cuentas distintas
 * compartirían valor.
 */
export function extractMasterOptionsDeVariasEstructuras(structuresByType, types = [], nombres = {}) {
  if (!types.length) return [];
  if (types.length === 1) {
    return extractMasterAllLevelOptions(structuresByType?.[types[0]]).map((o) => ({
      ...o,
      value: refDeCuentaMaestra(types[0], o.value),
      structureType: types[0],
    }));
  }

  const options = [];
  types.forEach((type) => {
    const deLaEstructura = extractMasterAllLevelOptions(structuresByType?.[type]);
    if (!deLaEstructura.length) return;
    options.push({
      key: `seccion-${type}`,
      value: `seccion-${type}`,
      text: nombres[type] || type,
      isSection: true,
    });
    deLaEstructura.forEach((o) =>
      options.push({ ...o, key: refDeCuentaMaestra(type, o.key), value: refDeCuentaMaestra(type, o.value), structureType: type }),
    );
  });
  return options;
}

export function findSelectableMasterOption(options, value) {
  return (options || []).find((opt) => !opt.isSection && opt.value === value);
}

/** Cuántas cuentas indivisibles tiene una estructura (para estados vacíos). */
export function countLeafAccounts(structure) {
  let total = 0;
  const walk = (nodes) => {
    (nodes || []).forEach((node) => {
      if (node.children?.length) walk(node.children);
      else total += 1;
    });
  };
  walk(structure?.accounts);
  return total;
}

/**
 * Hijas directas de una cuenta, o las raíces si `parentKey` viene vacío.
 * Espejo de getChildrenOf del backend; lo usa el reordenamiento por arrastre,
 * que necesita la lista completa de hermanas para recalcular el orden.
 */
export function getChildrenOf(accounts, parentKey) {
  if (!parentKey) return accounts || [];
  const parent = findNodeByKey(accounts, parentKey);
  return parent ? parent.children || [] : null;
}

/**
 * Marca cada nodo del árbol con la estructura a la que pertenece.
 *
 * La pantalla dibuja Activos y Pasivos y Patrimonio JUNTOS, bajo una sola
 * pestaña "Balance", y a partir de ahí la clave ya no alcanza para saber a qué
 * árbol pertenece una fila: "2.01.01" existe en los dos y significa cosas
 * distintas. La lleva el propio nodo, así que cualquier acción —renombrar,
 * mover, borrar, contar sus cuentas contables— sabe sobre qué estructura va
 * sin depender de cuál pestaña esté activa.
 */
export function marcarTipoEnArbol(nodes, structureType) {
  return (nodes || []).map((node) => ({
    ...node,
    structureType,
    children: marcarTipoEnArbol(node.children, structureType),
  }));
}
